"use client";

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import type { EventSummary } from "@/lib/types/eventList";
import { cardBadge, waitingListOpenFor } from "@/components/explore/eventCardModel";

export type Label = { text: string; tone: "amber" | "danger" | "brand" };

/**
 * The labels before the cards' titles -- Últimas vagas, Último lugar, Esgotado,
 * Novidade, Na lista de espera -- by event, from the list the browser asks for
 * after mount: the page itself is cached between visitors and carries none
 * (montanhas-web#16). Empty until the list answers. `going` is the events the
 * client is registered for, which a card then opens as theirs; `waiting` the
 * full ones whose waiting list the client could join (Lista de espera aberta);
 * `live` the list's events themselves, which Próximos eventos' cards are drawn
 * from once known (Início now asks for `live` alone).
 */
export function useLiveLabels(enabled = true) {
  const [state, setState] = useState<{ labels: Record<number, Label | null>; going: Record<number, boolean>; waiting: Record<number, boolean>; live: Record<number, EventSummary> }>({ labels: {}, going: {}, waiting: {}, live: {} });

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    api.get<EventSummary[]>("/public-api/events", { signal: controller.signal, silent: true }).then((events) => {
      if (!events || controller.signal.aborted) return;
      const labels: Record<number, Label | null> = {};
      const going: Record<number, boolean> = {};
      const waiting: Record<number, boolean> = {};
      const live: Record<number, EventSummary> = {};
      events.forEach((event) => {
        live[event.id] = event;
        labels[event.id] = cardBadge(event);
        going[event.id] = !!event.is_registered && !event.awaiting_payment;
        waiting[event.id] = waitingListOpenFor(event);
      });
      setState({ labels, going, waiting, live });
    });
    return () => controller.abort();
  }, [enabled]);

  return state;
}

export const LABEL_TONES: Record<Label["tone"], string> = { amber: "bg-amber", danger: "bg-danger", brand: "bg-brand" };

/**
 * A label before a card's title, in its line, as EventCard has it -- never over the
 * banner, which is a poster with its own words (the user's call). `small` for a
 * small card's.
 */
export function TitleLabel({ label, small = false }: { label: Label | null | undefined; small?: boolean }) {
  if (!label) return null;
  return (
    <>
      <span className={`relative -top-px mr-1.5 inline-block rounded-md px-2 py-0.5 align-middle font-bold leading-[1.45] tracking-normal text-white ${small ? "text-[10.5px]" : "text-[11.5px]"} ${LABEL_TONES[label.tone]}`}>
        {label.text}
      </span>{" "}
    </>
  );
}
